import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Card, CardMedia, CardContent,
  Chip, Avatar, Button, Divider, Skeleton, LinearProgress, IconButton, Tooltip
} from '@mui/material';
import {
  AccessTime, CalendarToday, ArrowBack, ArrowForward,
  Share, LinkedIn, Twitter, Facebook, Link as LinkIcon, ArrowUpward
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import SEO from '../../components/SEO/SEO';
import { blogAPI } from '../../services/api';

const FOREST = '#163300';
const GREEN  = '#9FE870';
const SITE   = window.location.hostname.includes('.pk')
  ? 'https://fluxerp.pk'
  : window.location.hostname.includes('.qa')
    ? 'https://fluxerp.qa'
    : 'https://fluxerp.co.uk';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : '';

// Reading progress bar
const ReadingProgress = () => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el  = document.documentElement;
      const top = el.scrollTop || document.body.scrollTop;
      const h   = el.scrollHeight - el.clientHeight;
      setPct(h > 0 ? Math.min(100, (top / h) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <LinearProgress variant="determinate" value={pct}
      sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: 3,
        bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: GREEN } }} />
  );
};

// Auto-generate TOC from h2/h3 in HTML string
const buildTOC = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const items = [];
  div.querySelectorAll('h2, h3').forEach((el, i) => {
    const id = `heading-${i}`;
    el.id = id;
    items.push({ id, text: el.textContent, level: el.tagName });
  });
  return { toc: items, html: div.innerHTML };
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const contentRef = useRef(null);
  const [activeId, setActiveId] = useState('');
  const [toc, setToc] = useState([]);
  const [processedHtml, setProcessedHtml] = useState('');

  const { data, isLoading, isError } = useQuery(
    ['blog', slug],
    () => blogAPI.getBySlug(slug).then(r => r.data.data),
    { 
      retry: false,
      initialData: window.__BLOG_DATA__
    }
  );

  const { data: related } = useQuery(
    ['blogRelated', data?.id],
    () => blogAPI.getRelated(data.id).then(r => r.data.data),
    { enabled: !!data?.id }
  );

  useEffect(() => {
    if (data?.content) {
      const { toc: t, html } = buildTOC(data.content);
      setToc(t);
      setProcessedHtml(html);
    }
  }, [data?.content]);

  // Intersection observer for active TOC item
  useEffect(() => {
    if (!toc.length) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }),
      { rootMargin: '-20% 0px -70% 0px' }
    );
    toc.forEach(({ id }) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [toc]);

  useEffect(() => { if (isError) navigate('/blog', { replace: true }); }, [isError, navigate]);

  const pageUrl = `${SITE}/blog/${slug}`;

  const copyLink = () => {
    navigator.clipboard.writeText(pageUrl);
    enqueueSnackbar('Link copied!', { variant: 'success' });
  };

  if (isLoading) return (
    <WebsiteLayout>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Skeleton height={60} width="70%" sx={{ mb: 2 }} />
        <Skeleton height={400} sx={{ borderRadius: 3, mb: 4 }} />
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>{[...Array(6)].map((_, i) => <Skeleton key={i} height={20} sx={{ mb: 1 }} />)}</Grid>
        </Grid>
      </Container>
    </WebsiteLayout>
  );

  if (!data) return null;

  const seo = {
    title: data.metaTitle || `${data.title} | FluxERP Blog`,
    description: data.metaDescription || data.excerpt,
    canonical: data.canonicalUrl || `/blog/${slug}`,
    ogType: 'article',
    ogImage: data.ogImage || data.featuredImage,
    ogImageAlt: data.title,
    noindex: !data.robotsIndex,
    publishedTime: data.publishDate,
    modifiedTime: data.updatedAt,
    author: data.author ? `${data.author.firstName} ${data.author.lastName}` : 'FluxERP Team',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: data.title,
        description: data.excerpt,
        image: data.featuredImage,
        datePublished: data.publishDate,
        dateModified: data.updatedAt,
        author: { '@type': 'Person', name: data.author ? `${data.author.firstName} ${data.author.lastName}` : 'FluxERP Team' },
        publisher: { '@type': 'Organization', name: 'FluxERP', logo: { '@type': 'ImageObject', url: `${SITE}/icons/icon-512x512.png` } },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE}/blog/${slug}` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
          ...(data.category ? [{ '@type': 'ListItem', position: 3, name: data.category.name, item: `${SITE}/blog?category=${data.category.slug}` }] : []),
          { '@type': 'ListItem', position: data.category ? 4 : 3, name: data.title, item: `${SITE}/blog/${slug}` },
        ],
      },
    ],
  };

  return (
    <WebsiteLayout>
      <SEO {...seo} />
      <ReadingProgress />

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4, flexWrap: 'wrap' }}>
          <Button component={Link} to="/" size="small" sx={{ color: 'text.secondary', fontWeight: 500, minWidth: 0, p: 0 }}>Home</Button>
          <Typography color="text.disabled">/</Typography>
          <Button component={Link} to="/blog" size="small" sx={{ color: 'text.secondary', fontWeight: 500, minWidth: 0, p: 0 }}>Blog</Button>
          {data.category && <>
            <Typography color="text.disabled">/</Typography>
            <Button component={Link} to={`/blog?category=${data.category.slug}`} size="small"
              sx={{ color: 'text.secondary', fontWeight: 500, minWidth: 0, p: 0 }}>{data.category.name}</Button>
          </>}
          <Typography color="text.disabled">/</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled',
            maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.title}</Typography>
        </Box>

        <Grid container spacing={5}>
          {/* Main content */}
          <Grid item xs={12} md={8}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {data.category && (
                  <Chip label={data.category.name} size="small"
                    sx={{ bgcolor: 'rgba(159,232,112,0.15)', color: FOREST, fontWeight: 700 }} />
                )}
                {(data.tags || []).map(tag => (
                  <Chip key={tag} label={tag} size="small" variant="outlined"
                    sx={{ borderColor: 'rgba(22,51,0,0.2)', color: 'text.secondary', fontSize: '0.72rem' }} />
                ))}
              </Box>

              <Typography variant="h1" sx={{ fontSize: { xs: 28, md: 42 }, fontWeight: 800, lineHeight: 1.2, mb: 2 }}>
                {data.title}
              </Typography>

              {data.excerpt && (
                <Typography variant="h6" sx={{ fontWeight: 400, color: 'text.secondary', lineHeight: 1.65, mb: 3 }}>
                  {data.excerpt}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', pb: 3,
                borderBottom: '1px solid', borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,51,0,0.08)' }}>
                {data.author && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: FOREST, color: GREEN, fontWeight: 800, fontSize: '0.85rem' }}>
                      {data.author.firstName?.[0]}{data.author.lastName?.[0]}
                    </Avatar>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      {data.author.firstName} {data.author.lastName}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{fmtDate(data.publishDate)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTime sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{data.readingTime} min read</Typography>
                </Box>
              </Box>
            </Box>

            {/* Featured image */}
            {data.featuredImage && (
              <Box component="img" src={data.featuredImage} alt={data.title} loading="lazy"
                sx={{ width: '100%', borderRadius: 3, mb: 5, maxHeight: 480, objectFit: 'cover' }} />
            )}

            {/* Content */}
            <Box
              ref={contentRef}
              dangerouslySetInnerHTML={{ __html: processedHtml }}
              sx={{
                '& h2': { fontSize: { xs: '1.4rem', md: '1.75rem' }, fontWeight: 800, mt: 5, mb: 2, color: FOREST },
                '& h3': { fontSize: { xs: '1.15rem', md: '1.35rem' }, fontWeight: 700, mt: 4, mb: 1.5 },
                '& p':  { lineHeight: 1.85, mb: 2, fontSize: '1.02rem' },
                '& ul, & ol': { pl: 3, mb: 2, '& li': { mb: 0.75, lineHeight: 1.75 } },
                '& blockquote': {
                  borderLeft: `4px solid ${GREEN}`, pl: 3, py: 1, my: 3,
                  bgcolor: 'rgba(159,232,112,0.06)', borderRadius: '0 8px 8px 0',
                  '& p': { mb: 0, fontStyle: 'italic', color: 'text.secondary' },
                },
                '& pre': {
                  bgcolor: (t) => t.palette.mode === 'dark' ? '#1a1a1a' : '#f4f4f4',
                  p: 2.5, borderRadius: 2, overflowX: 'auto', mb: 3,
                  '& code': { fontFamily: 'monospace', fontSize: '0.875rem' },
                },
                '& code': { bgcolor: 'rgba(22,51,0,0.07)', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.875em', fontFamily: 'monospace' },
                '& img': { maxWidth: '100%', borderRadius: 2, my: 2 },
                '& a': { color: FOREST, fontWeight: 600, '&:hover': { color: '#0b1a00' } },
                '& table': { width: '100%', borderCollapse: 'collapse', mb: 3,
                  '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1.5, textAlign: 'left' },
                  '& th': { bgcolor: 'rgba(22,51,0,0.05)', fontWeight: 700 },
                },
                '& hr': { my: 4, borderColor: 'divider' },
              }}
            />

            {/* Social share */}
            {data.allowSharing && (
              <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontWeight: 700, mb: 2, fontSize: '0.875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Share this article
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {[
                    { icon: <LinkedIn />, label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`, color: '#0A66C2' },
                    { icon: <Twitter />,  label: 'Twitter',  href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(data.title)}`, color: '#1DA1F2' },
                    { icon: <Facebook />, label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, color: '#1877F2' },
                  ].map(s => (
                    <Tooltip key={s.label} title={s.label}>
                      <IconButton href={s.href} target="_blank" rel="noopener noreferrer" size="small"
                        sx={{ bgcolor: s.color, color: 'white', '&:hover': { bgcolor: s.color, opacity: 0.85 }, width: 36, height: 36 }}>
                        {React.cloneElement(s.icon, { sx: { fontSize: 18 } })}
                      </IconButton>
                    </Tooltip>
                  ))}
                  <Tooltip title="Copy link">
                    <IconButton onClick={copyLink} size="small"
                      sx={{ bgcolor: FOREST, color: GREEN, '&:hover': { bgcolor: '#0b1a00' }, width: 36, height: 36 }}>
                      <LinkIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* Related articles */}
            {related?.length > 0 && (
              <Box sx={{ mt: 8 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>Related Articles</Typography>
                <Grid container spacing={3}>
                  {related.map(r => (
                    <Grid item xs={12} sm={6} md={4} key={r.id}>
                      <Card component={Link} to={`/blog/${r.slug}`} sx={{
                        textDecoration: 'none', borderRadius: 2, border: '1px solid',
                        borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,51,0,0.08)',
                        transition: 'all 0.25s', '&:hover': { transform: 'translateY(-4px)', borderColor: GREEN },
                      }}>
                        {r.featuredImage && <CardMedia component="img" image={r.featuredImage} alt={r.title} height={140} sx={{ objectFit: 'cover' }} />}
                        <CardContent sx={{ p: 2 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.4,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {r.title}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
                            {r.readingTime} min read
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* CTA */}
            <Box sx={{ mt: 8, p: { xs: 3, md: 5 }, borderRadius: 3, bgcolor: FOREST, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: GREEN, mb: 1 }}>
                See FluxERP in action
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', mb: 3, fontSize: '0.95rem' }}>
                Book a free 30-minute demo tailored to your business.
              </Typography>
              <Button component={Link} to="/demo" variant="contained"
                sx={{ bgcolor: GREEN, color: FOREST, fontWeight: 800, px: 4, '&:hover': { bgcolor: '#b6f192' } }}>
                Book a Demo
              </Button>
            </Box>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 90 }}>
              {/* TOC */}
              {toc.length > 0 && (
                <Card sx={{ p: 3, borderRadius: 3, mb: 3, border: '1px solid',
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,51,0,0.08)' }}>
                  <Typography sx={{ fontWeight: 800, mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 1, color: FOREST }}>
                    Table of Contents
                  </Typography>
                  {toc.map(item => (
                    <Box key={item.id} component="a" href={`#${item.id}`}
                      sx={{
                        display: 'block', py: 0.75, pl: item.level === 'H3' ? 2 : 0,
                        fontSize: '0.85rem', color: activeId === item.id ? FOREST : 'text.secondary',
                        fontWeight: activeId === item.id ? 700 : 400,
                        textDecoration: 'none', borderLeft: '2px solid',
                        borderColor: activeId === item.id ? GREEN : 'transparent',
                        pl: item.level === 'H3' ? 2.5 : 1,
                        transition: 'all 0.2s',
                        '&:hover': { color: FOREST },
                      }}>
                      {item.text}
                    </Box>
                  ))}
                </Card>
              )}

              {/* Back to top */}
              <Button fullWidth variant="outlined" startIcon={<ArrowUpward />}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                sx={{ borderColor: 'rgba(22,51,0,0.2)', color: FOREST, fontWeight: 600, borderRadius: 2, mb: 3 }}>
                Back to top
              </Button>

              {/* Back to blog */}
              <Button fullWidth component={Link} to="/blog" startIcon={<ArrowBack />}
                sx={{ color: 'text.secondary', fontWeight: 600, justifyContent: 'flex-start' }}>
                All Articles
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </WebsiteLayout>
  );
};

export default BlogDetailPage;
